import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  EntityConstants,
  IGenericRequestParameters,
  InferSortByOptions,
  isIVersionedEntity,
  IVersionedEntity,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { Button } from "../../../common/buttons/Button/Button";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { useGet, useRestoreVersionMutation } from "../../../api/BaseEntityApi";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { GenericEntityDetailTable, GenericEntityDetailTableProps } from "./GenericEntityDetailTable";
import { DetailsPageTopControlsView } from "../../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { GenericEntityTable } from "./GenericEntityTable";
import { AuditLog, auditLogConstants, AuditLogFieldLabels, AuditLogFilters } from "../../../api/AuditLog";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { AuditLogFilterBarProps, AuditLogFilterForm } from "../../../AuditLog/AuditLogFilterBar";
import { ToggleButtonComponent } from "../../../ViewerUIElements/ToggleButtonComponent";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { AuditLogDetail } from "../../../AuditLog/AuditLogsDetail";
import { useEntityApi } from "../../../api/useEntityApi";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { useEntityRoutes } from "../../../common/routes/useEntityRoutes";
import { renderError } from "../../../common/helperfunctions/ApiError";

type EntityHistoryViews = "events" | "versions";
interface GenericEntityHistoryModalProps<
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> {
  hideTriggerComponent?: boolean;
  triggerComponent?: ({ setIsOpen }: { setIsOpen: Dispatch<SetStateAction<boolean>> }) => JSX.Element;
  fetchParams?: Filters;
  detailTableProps?: GenericEntityDetailTableProps;
  onRestore?: () => void;
  forceOpen?: boolean;
  onModalClose?: () => void;
}

export const EntityHistoryModal = <
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntityHistoryModalProps<Entity, Filters>>) => {
  const { route } = useContext(SessionContext);
  const [historyModalIsOpen, setHistoryModalIsOpen] = useState<boolean>(false);
  const [currentVersion, setCurrentVersion] = useState<number>(entity?.version ?? 1);
  const historyModalIsOpenState = componentProps?.forceOpen ?? historyModalIsOpen;
  const [currentView, setCurrentView] = useState<EntityHistoryViews>("events");
  const [currentEntity, setCurrentEntity] = useState<Entity | undefined>();
  const [selectedAuditLogId, setSelectedAuditLogId] = useState<number | undefined>(undefined);

  useEffect(() => {
    entity?.version !== undefined && setCurrentVersion(entity.version);
  }, [entity]);

  useEffect(() => {
    setSelectedAuditLogId(undefined);
  }, [currentView]);

  const onCloseCallback = useCallback(() => {
    setHistoryModalIsOpen(false);
    componentProps?.onModalClose?.();
  }, [componentProps]);

  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { endpoint, queryKey } = useMemo(() => {
    return {
      endpoint: entityConstants.resource
        ? entity?.id
          ? (`${entityConstants.resource}/${entity.id}/${auditLogConstants.resource}` as any)
          : (`${entityConstants.resource}/${auditLogConstants.resource}` as any)
        : `${auditLogConstants.resource}`,
      queryKey: entityConstants.resource
        ? entity?.id
          ? [entityConstants.resource, entity.id, auditLogConstants.resource]
          : [entityConstants.resource, auditLogConstants.resource]
        : [auditLogConstants.resource],
    };
  }, [entity?.id, entityConstants.resource]);

  const [includeHierarchicalContent, setIncludeHierarchicalContent] = useState<boolean>(
    hierarchicalContentEntityTypes.includes(entityTypeId)
  );
  return (
    <>
      {(!componentProps?.hideTriggerComponent &&
        componentProps?.triggerComponent?.({ setIsOpen: setHistoryModalIsOpen })) ?? (
        <Button
          className="btn btn-default"
          onClick={() => setHistoryModalIsOpen(true)}
          disabled={!entity}
          title={`View ${entityConstants.entitySingular} history`}
        >
          <LucideIcon name="history" />
        </Button>
      )}

      {!!entity && historyModalIsOpenState && (
        <Modal isOpen={historyModalIsOpenState} onClose={onCloseCallback}>
          <Modal.Body
            style={{
              width: "90vw",
              height: "80vh",
              overflow: "hidden",
              display: "grid",
              gridTemplateRows: "auto 1fr auto",
              minWidth: "min-content",
              paddingBottom: "5px",
            }}
          >
            <EntityHistoryModalHeader<Entity, Filters>
              entityTypeId={entityTypeId}
              entity={entity}
              componentProps={componentProps}
              currentView={currentView}
              setCurrentView={setCurrentView}
              currentVersion={currentVersion}
              setCurrentVersion={setCurrentVersion}
              currentEntity={currentEntity}
              includeHierarchicalContent={includeHierarchicalContent}
              setIncludeHierarchicalContent={setIncludeHierarchicalContent}
              selectedAuditLogId={selectedAuditLogId}
              setSelectedAuditLogId={setSelectedAuditLogId}
            />

            {currentView === "events" && (
              <>
                {!!selectedAuditLogId ? (
                  <SelectedAuditLogDetailView
                    auditLogId={selectedAuditLogId}
                    entityConstants={entityConstants}
                    entityId={entity.id}
                  />
                ) : (
                  <div style={{ height: "100%" }}>
                    <GenericEntityTable<GenericEntity, AuditLogFilters, AuditLogFilterForm, AuditLogFilterBarProps>
                      entityTypeId="auditLogs"
                      componentProps={{
                        entityConstants: auditLogConstants,
                        overrideRessource: endpoint,
                        showPermissionColumn: false,
                        readonly: true,
                        useInfiniteQueryOptions: { queryKey, queryKeyIncludeFilters: true },
                        hideTabs: true,
                        hideHeaderControls: true,
                        hideTableControls: true,
                        hideSearch: true,
                        disableCheckboxes: true,
                        hideTitle: true,
                        containerStyle: { padding: 0, margin: 0 },
                        sidebarDefaultSize: 0,
                        additionalFilterbarComponentProps: { hideEntityTypeFilter: true },
                        noResultsMessage: `No ${auditLogConstants.entityPlural} found for this ${entityConstants.entitySingular}`,
                        preventIndexPermissionCheck: true,
                        forcedFilters: { includeHierarchicalAuditLogs: includeHierarchicalContent },
                        onRowClick: (auditLog) => setSelectedAuditLogId(auditLog.id),
                        linkTo: (auditLog) =>
                          route(
                            `/${entityConstants.resource}/${entity.id}/${auditLogConstants.resource}/${auditLog.id}`
                          ),
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {currentView === "versions" && (
              <EntityHistoryModalVersionView<Entity, Filters>
                entityTypeId={entityTypeId}
                entity={entity}
                componentProps={componentProps}
                currentView={currentView}
                currentVersion={currentVersion}
                setCurrentVersion={setCurrentVersion}
                setCurrentEntity={setCurrentEntity}
              />
            )}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export const GenericEntityHistoryModal = <
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntityHistoryModalProps<Entity, Filters>>) => {
  const defaultComponentProps = useDefaultGenericHistoryModal({ entityTypeId });

  return (
    <EntityHistoryModal
      entityTypeId={entityTypeId}
      entity={entity}
      componentProps={{ ...defaultComponentProps, ...componentProps }}
    />
  );
};

const useDefaultGenericHistoryModal: <
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
}: {
  entityTypeId: GenericEntityConstantsEntities;
}) => Partial<GenericEntityHistoryModalProps<Entity, Filters>> = ({ entityTypeId }) => {
  switch (entityTypeId) {
    case "notebookEntries":
    case "notebookTemplates":
      return { fetchParams: { includeContent: true } as any, detailTableProps: { includeContent: true } };
    default:
      return {};
  }
};

const hierarchicalContentEntityTypes: GenericEntityConstantsEntities[] = [
  "notebookEntries",
  "notebookExperiments",
  "notebooks",
  "inventoryItems",
];

interface EntityHistoryModalHeaderProps<
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> extends GenericEntityComponentProps<Entity, GenericEntityHistoryModalProps<Entity, Filters>> {
  currentView: EntityHistoryViews;
  setCurrentView: Dispatch<SetStateAction<EntityHistoryViews>>;
  currentVersion: number;
  setCurrentVersion: Dispatch<SetStateAction<number>>;
  currentEntity?: Entity;
  includeHierarchicalContent: boolean;
  setIncludeHierarchicalContent: Dispatch<SetStateAction<boolean>>;
  selectedAuditLogId?: AuditLog["id"];
  setSelectedAuditLogId: Dispatch<SetStateAction<AuditLog["id"] | undefined>>;
}
const EntityHistoryModalHeader = <
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  entity,
  componentProps,
  currentView,
  setCurrentView,
  currentEntity,
  includeHierarchicalContent,
  setIncludeHierarchicalContent,
  selectedAuditLogId,
  setSelectedAuditLogId,
}: EntityHistoryModalHeaderProps<Entity, Filters>) => {
  const [restoreModalIsOpen, setRestoreModalIsOpen] = useState<boolean>(false);
  const isVersioned = useMemo(() => isIVersionedEntity(entity), [entity]);
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const { mutateAsync } = useRestoreVersionMutation(entityConstants.resource);
  const restoreVersion = useCallback(async () => {
    if (entity && !!currentEntity?.version && entity.version !== currentEntity?.version) {
      await mutateAsync(
        { id: entity.id, version: currentEntity.version },
        { onSuccess: (data) => componentProps?.onRestore?.() }
      ).catch((error) => {});
      setRestoreModalIsOpen(false);
    }
  }, [componentProps, currentEntity, entity, mutateAsync]);

  if (!entity) return null;
  return (
    <>
      <div
        className="flex align-center"
        style={{
          borderBottom: "1px solid var(--gray-200)",
          marginBottom: "5px",
          padding: "5px 10px",
          paddingBottom: 0,
        }}
      >
        {isVersioned && (
          <div
            className="flex align-center gap-5"
            style={{
              flexDirection: "column",
              borderRight: "1px solid var(--gray-200)",
              paddingRight: "10px",
              paddingBottom: "5px",
              marginRight: "10px",
            }}
          >
            <button
              className={`flex align-center gap-5 btn ${currentView === "events" ? "btn-info" : "btn-soft-info"}`}
              style={{ width: "100%", textAlign: "left" }}
              onClick={() => {
                setSelectedAuditLogId(undefined);
                setCurrentView("events");
              }}
            >
              <div>
                <LucideIcon name="list" />
              </div>
              Events
            </button>
            <button
              className={`flex align-center gap-5 btn ${currentView === "versions" ? "btn-info" : "btn-soft-info"}`}
              style={{ width: "100%", textAlign: "left" }}
              onClick={() => setCurrentView("versions")}
            >
              <div>
                <LucideIcon name="history" />
              </div>
              Versions
            </button>
          </div>
        )}

        <div style={{ width: "100%", padding: isVersioned ? 0 : "5px" }}>
          <DetailsPageTopControlsView
            hideHistoryModal
            entityTypeId={entityTypeId}
            containerStyle={{ border: 0, gap: "15px", padding: 0, margin: 0, height: "100%" }}
            entityConstants={entityConstants}
            entity={currentView === "versions" ? currentEntity ?? entity : entity}
            nameOverride={
              isVersioned && currentView === "versions" && !!currentEntity?.version
                ? `${!!entity.name ? entity.name : `ID: ${entity.id}`} - ${
                    currentEntity.version === entity.version
                      ? "Current version"
                      : `Version ${currentEntity.version} of ${entity.version}`
                  }`
                : `${!!entity.name ? entity.name : `ID: ${entity.id}`} - ${toUppercase(auditLogConstants.entityPlural)}`
            }
            controls={
              <>
                {isVersioned && currentView === "versions" && (
                  <Button
                    className="btn btn-success"
                    disabled={!currentEntity?.version || currentEntity?.version === entity.version}
                    onClick={() => setRestoreModalIsOpen(true)}
                  >
                    Restore
                  </Button>
                )}
                {currentView === "events" &&
                  hierarchicalContentEntityTypes.includes(entityTypeId) &&
                  !selectedAuditLogId && (
                    <div
                      className="btn btn-default"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIncludeHierarchicalContent((prevState) => !prevState);
                      }}
                    >
                      <ToggleButtonComponent
                        checked={includeHierarchicalContent}
                        // setChecked={setIncludeHierarchicalContent}
                      >
                        Include hierarchical content
                      </ToggleButtonComponent>
                    </div>
                  )}
                {currentView === "events" && !!selectedAuditLogId && (
                  <Button
                    className="btn btn-primary btn-sm flex align-center gap-5"
                    onClick={() => setSelectedAuditLogId(undefined)}
                  >
                    <LucideIcon name="arrow-left" />
                    Back to table
                  </Button>
                )}
              </>
            }
          />
        </div>
      </div>

      {!!currentEntity?.version && (
        <AlertModal
          showModal={restoreModalIsOpen}
          setShowModal={setRestoreModalIsOpen}
          onCancel={() => setRestoreModalIsOpen(false)}
          onProceed={restoreVersion}
          title={`Restore ${entityConstants.entitySingular} to version ${currentEntity?.version}?`}
          description={`This will replace the current version of the ${entityConstants.entitySingular} with the selected version.`}
          proceedLabel="Restore"
          type="warning"
        />
      )}
    </>
  );
};

interface EntityHistoryModalVersionViewProps<
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> extends GenericEntityComponentProps<Entity, GenericEntityHistoryModalProps<Entity, Filters>> {
  currentView: EntityHistoryViews;
  currentVersion: number;
  setCurrentVersion: Dispatch<SetStateAction<number>>;
  setCurrentEntity: Dispatch<SetStateAction<Entity | undefined>>;
}
const EntityHistoryModalVersionView = <
  Entity extends GenericEntity & IVersionedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  entity,
  componentProps,
  currentView,
  currentVersion,
  setCurrentVersion,
  setCurrentEntity,
}: EntityHistoryModalVersionViewProps<Entity, Filters>) => {
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const {
    data: currentEntity,
    isFetching: isFetchingEntityVersion,
    fetchStatus: fetchStatusEntityVersion,
    status: statusEntityVersion,
    error: errorEntityVersion,
  } = useGet<Entity>(
    `${entityConstants.resource}/${entity?.id ?? 0}/versions/${currentVersion}`,
    componentProps?.fetchParams,
    {
      enabled: currentView === "versions",
      queryKey: [entityConstants.resource, entity?.id, "versions", currentVersion],
      onSuccess: (data) => data && setCurrentEntity(data),
    }
  );

  return (
    <>
      <div style={{ overflow: "auto" }}>
        <div style={{ width: "100%", padding: "0 10px", height: "100%" }}>
          <LoadingWrapper
            fetchStatus={isFetchingEntityVersion ? "fetching" : fetchStatusEntityVersion}
            status={isFetchingEntityVersion ? "loading" : statusEntityVersion}
            error={errorEntityVersion}
          >
            <GenericEntityDetailTable
              entityTypeId={entityTypeId}
              entity={currentEntity}
              componentProps={componentProps?.detailTableProps}
            />
          </LoadingWrapper>
        </div>
      </div>

      {!!entity && (
        <div
          className="flex justify-center align-center gap-5"
          style={{
            borderTop: "1px solid var(--gray-200)",
            padding: "5px 10px",
            minWidth: "max-content",
          }}
        >
          <button
            disabled={currentVersion <= 1}
            onClick={() => setCurrentVersion(1)}
            className="btn btn-secondary btn-xs"
            data-toggle="tooltip"
            title="Jump to first version"
          >
            | <LucideIcon name="chevrons-left" />
          </button>
          <button
            disabled={currentVersion <= 1}
            onClick={() => setCurrentVersion((prevState) => (prevState - 5 <= 1 ? 1 : prevState - 5))}
            className="btn btn-secondary btn-xs"
          >
            <LucideIcon name="chevrons-left" />
          </button>
          <button
            disabled={currentVersion <= 1}
            onClick={() => setCurrentVersion((prevState) => prevState - 1)}
            className="btn btn-secondary btn-xs"
            data-toggle="tooltip"
            title="Previous"
          >
            <LucideIcon name="chevron-left" /> Prev
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            {entity && entity.version === currentVersion ? (
              <div style={{ display: "flex" }}>
                <label
                  style={{
                    padding: "5px 20px",
                    margin: "0 10px",
                    overflow: "auto",
                    minWidth: "200px",
                    width: "100%",
                  }}
                  className="label label-soft-primary"
                >
                  Current
                </label>
              </div>
            ) : (
              <div style={{ display: "flex" }}>
                <label
                  className="label label-soft-secondary"
                  style={{ padding: "5px 20px", margin: "0 10px", minWidth: "200px" }}
                >
                  {`Version ${currentVersion} of ${entity.version}`}
                </label>
              </div>
            )}
          </div>
          <button
            disabled={currentVersion >= entity.version}
            onClick={() => setCurrentVersion((prevState) => prevState + 1)}
            className="btn btn-secondary btn-xs"
            data-toggle="tooltip"
            title="Next"
          >
            Next <LucideIcon name="chevron-right" />
          </button>
          <button
            disabled={currentVersion >= entity.version}
            onClick={() =>
              setCurrentVersion((prevState) => (prevState + 5 >= entity.version ? entity.version : prevState + 5))
            }
            className="btn btn-secondary btn-xs"
          >
            <LucideIcon name="chevrons-right" />
          </button>
          <button
            disabled={currentVersion >= entity.version}
            onClick={() => setCurrentVersion(entity.version)}
            className="btn btn-secondary btn-xs"
            data-toggle="tooltip"
            title="Jump to last version"
          >
            <LucideIcon name="chevrons-right" /> |
          </button>
        </div>
      )}
    </>
  );
};

const SelectedAuditLogDetailView = ({
  auditLogId,
  entityConstants,
  entityId,
}: {
  auditLogId: AuditLog["id"];
  entityConstants: EntityConstants;
  entityId?: GenericEntity["id"];
}) => {
  const {
    data: auditLog,
    isFetching,
    status,
    fetchStatus,
    error,
  } = useGet<AuditLog, AuditLogFilters>(
    `${entityConstants.resource}/${entityId ?? 0}/${auditLogConstants.resource}/${auditLogId ?? 0}`,
    {},
    {
      enabled: !!entityId && !!auditLogId,
      onError: (err) => {
        renderError(err);
      },
    }
  );

  const entityApi = useEntityApi<AuditLog>(auditLogConstants.resource);

  const permissions = useEntityPermissions({ entityTypeId: "auditLogs" });

  const routes = useEntityRoutes({ entityConstants: auditLogConstants });

  if (!entityId || !auditLog) return null;
  return (
    <div style={{ height: "100%" }}>
      <LoadingWrapper status={status} fetchStatus={isFetching ? "fetching" : fetchStatus} error={error}>
        <AuditLogDetail
          entity={auditLog}
          entityTypeId="auditLogs"
          entityApi={entityApi}
          entityConstants={auditLogConstants}
          fieldLabels={AuditLogFieldLabels}
          permissions={permissions}
          routes={routes}
        />
      </LoadingWrapper>
    </div>
  );
};
